import { useEffect, useRef } from "react";
import { RecaptchaVerifier } from "firebase/auth";
import { authentication } from "../firebase";

// Utility function to completely reset reCAPTCHA state
const resetRecaptchaState = () => {
  // Clear any global reCAPTCHA state
  if (window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch (error) {
      console.log("Error clearing global verifier:", error);
    }
    window.recaptchaVerifier = null;
  }

  // Remove any existing reCAPTCHA scripts from the entire document
  const existingScripts = document.querySelectorAll("script[src*='recaptcha']");
  existingScripts.forEach(script => {
    if (script.parentNode) {
      script.parentNode.removeChild(script);
    }
  });

  // Remove any reCAPTCHA iframes
  const recaptchaIframes = document.querySelectorAll("iframe[src*='recaptcha']");
  recaptchaIframes.forEach(iframe => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  });

  // Remove any reCAPTCHA divs except our container
  const recaptchaDivs = document.querySelectorAll("div[class*='recaptcha']");
  recaptchaDivs.forEach(div => {
    if (div.parentNode && div.id !== "recaptcha-container" && div.id !== "recaptcha-container-new") {
      div.parentNode.removeChild(div);
    }
  });

  // Clear any reCAPTCHA elements from the body
  const recaptchaElements = document.querySelectorAll("[class*='recaptcha']");
  recaptchaElements.forEach(el => {
    if (el.parentNode && el.id !== "recaptcha-container" && el.id !== "recaptcha-container-new") {
      el.parentNode.removeChild(el);
    }
  });
};

export const useRecaptcha = () => {
  const verifierRef = useRef(null);
  const isGeneratingRef = useRef(false);

  const generateRecaptcha = async () => {
    // Prevent multiple simultaneous calls
    if (isGeneratingRef.current) {
      throw new Error("reCAPTCHA generation already in progress");
    }
    
    isGeneratingRef.current = true;

    try {
      // Clear any existing verifier
      if (verifierRef.current) {
        try {
          verifierRef.current.clear();
        } catch (error) {
          console.log("Error clearing previous verifier:", error);
        }
        verifierRef.current = null;
      }

      // Reset all reCAPTCHA state
      resetRecaptchaState();

      // Get the container
      let container = document.getElementById("recaptcha-container");
      
      // If container doesn't exist, create it
      if (!container) {
        container = document.createElement("div");
        container.id = "recaptcha-container";
        document.body.appendChild(container);
      } else {
        // Completely remove and recreate the container
        container.remove();
        container = document.createElement("div");
        container.id = "recaptcha-container";
        document.body.appendChild(container);
      }

      // Wait for DOM to be cleared
      await new Promise(resolve => setTimeout(resolve, 200));

      // Create new verifier
      verifierRef.current = new RecaptchaVerifier(
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => console.log("reCAPTCHA solved"),
          "expired-callback": () => console.log("reCAPTCHA expired"),
        },
        authentication
      );

      return verifierRef.current;
    } catch (error) {
      console.error("Error creating reCAPTCHA verifier:", error);
      
      // If there's still an error, try one more time with a completely fresh approach
      try {
        // Reset state again
        resetRecaptchaState();
        
        // Remove the container completely
        const container = document.getElementById("recaptcha-container");
        if (container) {
          container.remove();
        }

        // Create a new container with a unique ID
        const newContainer = document.createElement("div");
        newContainer.id = "recaptcha-container-new";
        document.body.appendChild(newContainer);

        // Wait a bit more
        await new Promise(resolve => setTimeout(resolve, 300));
        
        verifierRef.current = new RecaptchaVerifier(
          "recaptcha-container-new",
          {
            size: "invisible",
            callback: () => console.log("reCAPTCHA solved"),
            "expired-callback": () => console.log("reCAPTCHA expired"),
          },
          authentication
        );
        
        return verifierRef.current;
      } catch (retryError) {
        console.error("Retry also failed:", retryError);
        throw retryError;
      }
    } finally {
      isGeneratingRef.current = false;
    }
  };

  const forceReset = () => {
    resetRecaptchaState();
    if (verifierRef.current) {
      try {
        verifierRef.current.clear();
      } catch (error) {
        console.log("Error clearing verifier:", error);
      }
      verifierRef.current = null;
    }
    isGeneratingRef.current = false;
  };

  useEffect(() => {
    return () => {
      if (verifierRef.current) {
        try {
          verifierRef.current.clear();
        } catch (error) {
          console.log("Error clearing verifier on unmount:", error);
        }
        verifierRef.current = null;
      }
      
      // Also clear global state on unmount
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (error) {
          console.log("Error clearing global verifier on unmount:", error);
        }
        window.recaptchaVerifier = null;
      }
      
      isGeneratingRef.current = false;
    };
  }, []);

  return { generateRecaptcha, forceReset };
};
